/**
 * The Admin's **Perfis de acesso** console — pure view-model (W9-PROFILES-REGISTRIES-KEYS, D475, ADR-0093; DevMobility
 * A-018/A-019; owner decision D461 §5 #11).
 *
 * A profile is a SUBSET of a base role's words with a home screen. This module holds what can be tested without React: the editor
 * draft (which words are ticked, by resource), the same validation the server runs (so the operator sees the problem before a round
 * trip), whether a draft needs a second operator, the diff between two versions, and which versions the reader may decide. The
 * server stays the authority on every rule — vocabulary, base role, four eyes, the retirement guard.
 */
import { type Result } from '@vello/core';
import {
  ADMIN_AUTHORITY_WORDS, PROFILE_BASE_ROLES, PROFILE_HOME_SCREENS, PROFILE_NAME_MAX, PROFILE_REASON_CODES, profileNeedsFourEyes, validateProfile,
  wordsOfferedByRole, type ProfileIssue,
} from '@vello/rbac';
import { type AccessProfile, type AccessProfileVersion, type ProfileDraftInput, type ProfileReasonCode } from '@vello/api-client';

export { ADMIN_AUTHORITY_WORDS, PROFILE_BASE_ROLES, PROFILE_HOME_SCREENS, PROFILE_NAME_MAX, PROFILE_REASON_CODES, profileNeedsFourEyes };
export type { ProfileIssue, ProfileReasonCode };

export interface ProfileForm {
  readonly name: string;
  readonly baseRole: string;
  /** The ticked words, in no particular order. */
  readonly words: readonly string[];
  readonly homeScreen: string;
  readonly note: string;
}

export function emptyProfileForm(): ProfileForm {
  return { name: '', baseRole: '', words: [], homeScreen: '', note: '' };
}

/** A form that starts from a profile's live words (to change them) — the base role is fixed by the profile. */
export function formFromProfile(p: AccessProfile): ProfileForm {
  return { name: p.name, baseRole: p.baseRole, words: p.live?.permissions ?? [], homeScreen: p.live?.homeScreen ?? '', note: '' };
}

/** The words the base role offers, grouped by resource (`ride`, `payout`…), each group sorted. */
export function wordGroups(role: string): readonly { readonly resource: string; readonly words: readonly string[] }[] {
  const groups = new Map<string, string[]>();
  for (const word of wordsOfferedByRole(role)) {
    const resource = word.split(':')[0] as string;
    const list = groups.get(resource) ?? [];
    list.push(word);
    groups.set(resource, list);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([resource, words]) => ({ resource, words: words.sort() }));
}

/** Tick or untick one word. Changing the base role drops every word it does not offer (never a stale word the server would refuse). */
export function toggleWord(form: ProfileForm, word: string): ProfileForm {
  const has = form.words.includes(word);
  return { ...form, words: has ? form.words.filter((w) => w !== word) : [...form.words, word] };
}

export function setBaseRole(form: ProfileForm, baseRole: string): ProfileForm {
  const offered = new Set(baseRole === '' ? [] : wordsOfferedByRole(baseRole));
  return { ...form, baseRole, words: form.words.filter((w) => offered.has(w)) };
}

/** Tick or untick a whole resource group. */
export function toggleGroup(form: ProfileForm, groupWords: readonly string[]): ProfileForm {
  const all = groupWords.every((w) => form.words.includes(w));
  const rest = form.words.filter((w) => !groupWords.includes(w));
  return { ...form, words: all ? rest : [...rest, ...groupWords] };
}

/** True when the draft's words reach admin authority (it will wait for a second operator). */
export function formNeedsFourEyes(form: ProfileForm): boolean {
  return profileNeedsFourEyes(form.words);
}

/** The words of the draft that are admin-authority words (to point at them in the editor). */
export function authorityWordsOf(words: readonly string[]): readonly string[] {
  return words.filter((w) => (ADMIN_AUTHORITY_WORDS as readonly string[]).includes(w));
}

export type ProfileFormIssue = ProfileIssue;

/** The same validation the server runs, as a `Result` carrying the request to send. */
export function normalizeProfileForm(form: ProfileForm, reasonCode: ProfileReasonCode): Result<ProfileDraftInput, readonly ProfileFormIssue[]> {
  const checked = validateProfile({ name: form.name, baseRole: form.baseRole, permissions: form.words, ...(form.homeScreen !== '' ? { homeScreen: form.homeScreen } : {}) });
  if (!checked.ok) return { ok: false, error: checked.issues };
  const note = form.note.trim();
  return {
    ok: true,
    value: {
      name: checked.profile.name, baseRole: checked.profile.baseRole, permissions: checked.profile.permissions,
      ...(checked.profile.homeScreen !== undefined ? { homeScreen: checked.profile.homeScreen } : {}), reasonCode, ...(note !== '' ? { note } : {}),
    },
  };
}

/** What changed between two versions' words: added and removed, each sorted. */
export function diffWords(before: readonly string[], after: readonly string[]): { readonly added: readonly string[]; readonly removed: readonly string[] } {
  const b = new Set(before);
  const a = new Set(after);
  return { added: after.filter((w) => !b.has(w)).sort(), removed: before.filter((w) => !a.has(w)).sort() };
}

/**
 * The version a reader may decide: PENDING and not its own proposal (the server and the database refuse a self-approval) -
 * unless (D868) the reader is a GLOBAL super administrator (`selfApproval`), who approves their own.
 */
export function canDecideVersion(v: AccessProfileVersion, selfApproval = false): boolean {
  return v.state === 'PENDING' && v.fourEyes && (v.mine !== true || selfApproval);
}

/** The proposer's own withdrawal. */
export function canWithdrawVersion(v: AccessProfileVersion): boolean {
  return v.state === 'PENDING' && v.mine === true;
}

/** A profile can be retired only while nothing waits on it and no live grant names it (the server refuses otherwise). */
export function canRetire(p: AccessProfile): boolean {
  return !p.retired && p.pending === undefined && p.liveGrants === 0;
}
